import type { ReactNode } from 'react'

import { ScreenHeader, type ScreenHeaderProps } from './ScreenHeader'

export interface ScreenLayoutProps extends ScreenHeaderProps {
  /**
   * Screen content. Renders inside a max-w-[540px] centered column.
   * The content area has no built-in horizontal padding — each screen
   * adds its own so it can control full-bleed vs padded regions independently.
   */
  children: ReactNode
}

/**
 * Shell for every booking-tool screen:
 *   - Full-height flex column with `bg-surface font-sans`
 *   - `ScreenHeader` spanning the full viewport width
 *   - Content area capped at 540 px, horizontally centered
 */
export const ScreenLayout = ({
  title,
  titleTestId,
  onClose,
  hasDropdown,
  onBack,
  children,
}: ScreenLayoutProps) => (
  <div className="flex flex-col h-full bg-surface font-sans">
    <ScreenHeader
      title={title}
      titleTestId={titleTestId}
      onClose={onClose}
      hasDropdown={hasDropdown}
      onBack={onBack}
    />
    {/*
      `min-h-0` unconditionally: a flex child's automatic minimum size is its
      content, so without it this column cannot shrink inside the `h-full`
      above. It grew to fit instead, and a screen that pins its footer as a
      `shrink-0` row — Timeslots, UserData, Confirmation — had that footer
      pushed past the bottom of the panel, which is why Continue needed
      scrolling once a timeslot was selected and the button row appeared.

      Screens own their own scrolling inside this column; `overflow-hidden`
      keeps a screen that forgets to from scrolling this level instead.
    */}
    <div
      className={`flex-1 flex flex-col w-full px-16 mx-auto min-h-0 ${hasDropdown ? '' : 'overflow-hidden'}`}
    >
      {children}
    </div>
  </div>
)

export default ScreenLayout
